// src/pages/VotacionesPage.js
import React, { useState, useEffect } from 'react';
import { Plus, Vote, ThumbsUp, ThumbsDown, Minus } from 'lucide-react';
import { Card, Button, Input, Select, Textarea, Modal, Badge } from '../components/ui';
import { votacionesService } from '../services/api';
import { formatDateTime } from '../utils/formatters';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const VotacionesPage = () => {
  const { hasPermission } = useAuth();
  const [votaciones, setVotaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    titulo: '', descripcion: '', tipo: 'ORDINARIA', fecha_fin: '', quorum: 50, tipo_aprobacion: 'MAYORIA_SIMPLE'
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await votacionesService.getAll();
      setVotaciones(response.data.data || []);
    } catch (error) {
      toast.error('Error al cargar votaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await votacionesService.create(form);
      toast.success('Votación creada');
      setModalOpen(false);
      loadData();
    } catch (error) {
      toast.error('Error al crear');
    }
  };

  const handleVotar = async (id, opcion) => {
    try {
      await votacionesService.votar(id, opcion);
      toast.success('Voto registrado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al votar');
    }
  };

  const handleFinalizar = async (id) => {
    try {
      await votacionesService.finalizar(id);
      toast.success('Votación finalizada');
      loadData();
    } catch (error) {
      toast.error('Error al finalizar');
    }
  };

  const statusVariant = { ACTIVA: 'success', FINALIZADA: 'info', CANCELADA: 'danger' };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Votaciones</h1>
          <p className="text-slate-400">Votaciones y decisiones de junta</p>
        </div>
        {hasPermission('PRESIDENTE', 'SECRETARIO') && (
          <Button icon={Plus} onClick={() => setModalOpen(true)}>Nueva Votación</Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {votaciones.map((v) => (
          <Card key={v.id} className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">{v.titulo}</h3>
                <p className="text-slate-400 text-sm mt-1">{v.descripcion}</p>
              </div>
              <Badge variant={statusVariant[v.estado]}>{v.estado}</Badge>
            </div>

            {v.resultados && (
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-emerald-500/10 rounded-lg p-3 text-center">
                  <ThumbsUp className="text-emerald-400 mx-auto mb-1" size={20} />
                  <p className="text-xl font-bold text-emerald-400">{v.resultados.aFavor}</p>
                  <p className="text-xs text-slate-500">A favor</p>
                </div>
                <div className="bg-rose-500/10 rounded-lg p-3 text-center">
                  <ThumbsDown className="text-rose-400 mx-auto mb-1" size={20} />
                  <p className="text-xl font-bold text-rose-400">{v.resultados.enContra}</p>
                  <p className="text-xs text-slate-500">En contra</p>
                </div>
                <div className="bg-slate-500/10 rounded-lg p-3 text-center">
                  <Minus className="text-slate-400 mx-auto mb-1" size={20} />
                  <p className="text-xl font-bold text-slate-400">{v.resultados.abstencion}</p>
                  <p className="text-xs text-slate-500">Abstención</p>
                </div>
              </div>
            )}

            <div className="text-sm text-slate-500 mb-4">
              Cierra: {formatDateTime(v.fecha_fin)} | Quórum: {v.quorum}%
            </div>

            {v.estado === 'ACTIVA' && (
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => handleVotar(v.id, 'A_FAVOR')} icon={ThumbsUp}>A favor</Button>
                <Button size="sm" variant="secondary" onClick={() => handleVotar(v.id, 'EN_CONTRA')} icon={ThumbsDown}>En contra</Button>
                <Button size="sm" variant="ghost" onClick={() => handleVotar(v.id, 'ABSTENCION')}>Abstención</Button>
              </div>
            )}

            {v.resultado && (
              <Badge variant={v.resultado === 'APROBADA' ? 'success' : 'danger'} size="lg">
                {v.resultado}
              </Badge>
            )}
          </Card>
        ))}
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Nueva Votación">
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input label="Título" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} required />
          <Textarea label="Descripción" value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} required />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Fecha Cierre" type="datetime-local" value={form.fecha_fin} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} required />
            <Input label="Quórum (%)" type="number" min="1" max="100" value={form.quorum} onChange={(e) => setForm({ ...form, quorum: e.target.value })} />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit">Crear Votación</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default VotacionesPage;